/**
 * Lesson Flow Context — [BUT-843, ours].
 *
 * Flow state for `/lessons/book`, seeded from and mirrored to the URL search
 * params so the flow is shareable, reloadable, and back-dismissable while
 * the middle two steps are still SCAFFOLD (`lessonAvailabilitySource.ts`).
 * See the plan's (a)/(d) sections for the full picture — this docblock only
 * covers the decisions load-bearing for this module.
 *
 * **Why not `BookingContext`.** `BookingContextValue` is booking-shaped
 * (guest count, guest user, payment token, available bays) and its
 * `setSelectedDate` clears both times; bending it to also carry
 * `instructorId` would be a shared-context edit with no upside. This
 * provider is additive and cannot regress the standard flow. `BookingProvider
 * persist={false}` is still mounted around the lesson route (by the route
 * config, not here) because `useWaiverSubmission` / `WaiverSection` read
 * `useBooking()` for `waiverSignatures` / `waiverAgreements` — `persist={false}`
 * for the same reason the demo route uses it: lesson selections must not leak
 * into the standard flow's restored sessionStorage, and there is no payment
 * redirect to survive.
 *
 * **Why these five params aren't in `~/lib/queryParams`'s `QueryParams`
 * enum.** That enum holds cross-cutting plumbing (`tenant-handle`,
 * `club-id`, …) that `TenantLink` / `useTenantNavigate` preserve app-wide. The
 * five lesson params belong to ONE page and must NOT be preserved onto other
 * routes, so they live here as their own frozen constant instead.
 *
 * **Behaviour — four rules:**
 * 1. Hydrate once, from the URL, in a lazy `useState` initializer — not an
 *    effect, not a sync-on-every-render (`docs/conventions/react-state.md`
 *    § "Every value has one home": the URL is the home, the state below is
 *    its working copy).
 * 2. Every mutator writes the URL in the same call, through
 *    `useTenantSearchParams` (a merge that replaces the history entry), so
 *    `club-id` / `tenant-handle` survive untouched and Back dismisses a step
 *    instead of leaving the portal. Replacing keeps one history entry per
 *    STEP, not per keystroke.
 * 3. `step` is seeded once from the hydrated selection and then owned by the
 *    user — deliberately NOT continuously derived, so a member who clicks
 *    "back" with a complete selection stays on that step.
 * 4. Cascade invalidation mirrors `BookingContext`'s own rule
 *    (`../../booking/context/BookingContext.tsx:262-267`): choosing a new
 *    instructor clears bay/date/times (the whole downstream selection was
 *    resolved against the old instructor's schedule); changing the date
 *    clears the times (times are date-specific).
 *
 * Wall-clock `HH:MM` strings are converted to UTC exactly once, at submit
 * (step 13's `LessonPaymentStep`), via `createClubDateTimeFromSlot` — never
 * `createClubDateTime` (`docs/conventions/slot-times.md`: an `endTime` may be
 * the `"24:00"` sentinel, which `createClubDateTime` rejects). This module
 * never round-trips them through a browser-local `Date` and never compares
 * them lexically — `durationMinutes` below uses `timeStringToMinutes`, the
 * sentinel-aware minute grammar.
 *
 * @module lessons/context/LessonFlowContext
 */

import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTenantSearchParams } from '~/lib/navigation';
import type { ImageData } from '@lgc/database/types/domains/storage';
import { DEFAULT_LESSON_DURATION_MINUTES } from '@butter/library/availability';
import { isValidDateString, isValidSlotTime, timeStringToMinutes, type DateString } from '@butter/library/datetime';
import {
  saveLessonDisplayExtras,
  loadLessonDisplayExtras,
  clearLessonDisplayExtras,
} from './sessionStorage';

/**
 * The lesson flow's own search params. NOT in `~/lib/queryParams`'s
 * `QueryParams` enum on purpose — see the module docblock.
 */
export const LESSON_SEARCH_PARAMS = Object.freeze({
  INSTRUCTOR_ID: 'instructorId',
  BAY_ID: 'bayId',
  DATE: 'date',
  START_TIME: 'startTime',
  END_TIME: 'endTime',
} as const);

export interface LessonFlowSelection {
  instructorId: number | null;
  bayId: number | null;
  /** YYYY-MM-DD, club-local. */
  date: DateString | null;
  /** HH:MM club wall clock. `endTime` may be `"24:00"` — slot-times.md. */
  startTime: string | null;
  endTime: string | null;
}

export interface LessonFlowContextValue extends LessonFlowSelection {
  /** Display-only, from sessionStorage; every screen degrades without them. */
  instructorName: string | null;
  instructorImage: ImageData | null;
  bayName: string | null;

  /** 0 = Instructor · 1 = Lesson Time · 2 = Confirmation. */
  step: 0 | 1 | 2;
  goToStep: (step: 0 | 1 | 2) => void;

  selectInstructor: (instructor: { id: number; name: string; image: ImageData | null }) => void;
  selectBay: (bay: { id: number; name: string }) => void;
  setDate: (date: DateString) => void;
  /**
   * `onDate` commits the day the time belongs to in the SAME write. The picker
   * shows the club's today before any date is chosen, so a member can pick a
   * time without ever touching the date — and a separate `setDate` call would
   * be overwritten by this one, since both build on the same render's selection.
   */
  setTimeRange: (range: { startTime: string; endTime: string } | null, onDate?: DateString) => void;
  clearSelection: () => void;

  /** Minutes between startTime and endTime, or null. Derived in render, never stored. */
  durationMinutes: number | null;
  /** The length the member is booking. Seeded from a deep-linked range, else the default. */
  lessonLength: number;
  /** A new length changes which starts fit, so the picked time (and bay) are cleared with it. */
  setLessonLength: (minutes: number) => void;
}

export const LessonFlowContext = createContext<LessonFlowContextValue | null>(null);

/** A malformed or absent id param reads as "not selected", never as `0`/`NaN`. */
function parseIdParam(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Parses the five lesson params off the current URL. Every value is screened
 * before it enters state — a malformed URL degrades to "not selected"
 * (step 0) rather than propagating garbage into a picker or a submit body.
 */
function readSelectionFromSearch(searchParams: URLSearchParams): LessonFlowSelection {
  const dateParam = searchParams.get(LESSON_SEARCH_PARAMS.DATE);
  const startTimeParam = searchParams.get(LESSON_SEARCH_PARAMS.START_TIME);
  const endTimeParam = searchParams.get(LESSON_SEARCH_PARAMS.END_TIME);

  return {
    instructorId: parseIdParam(searchParams.get(LESSON_SEARCH_PARAMS.INSTRUCTOR_ID)),
    bayId: parseIdParam(searchParams.get(LESSON_SEARCH_PARAMS.BAY_ID)),
    date: dateParam && isValidDateString(dateParam) ? dateParam : null,
    startTime: startTimeParam && isValidSlotTime(startTimeParam) ? startTimeParam : null,
    endTime: endTimeParam && isValidSlotTime(endTimeParam) ? endTimeParam : null,
  };
}

/** `2` when the whole selection is present, `1` when an instructor is chosen, else `0`. */
function deriveInitialStep(selection: LessonFlowSelection): 0 | 1 | 2 {
  const { instructorId, bayId, date, startTime, endTime } = selection;
  if (instructorId !== null && bayId !== null && date !== null && startTime !== null && endTime !== null) {
    return 2;
  }
  if (instructorId !== null) {
    return 1;
  }
  return 0;
}

interface LessonFlowProviderProps {
  children: ReactNode;
}

export function LessonFlowProvider({ children }: LessonFlowProviderProps) {
  const [searchParams, setParams] = useTenantSearchParams();

  // Rule 1 — hydrate once, from the URL, in a lazy initializer.
  const [selection, setSelection] = useState<LessonFlowSelection>(() =>
    readSelectionFromSearch(searchParams),
  );
  // Rule 3 — seeded once, then owned by the user.
  const [step, setStep] = useState<0 | 1 | 2>(() => deriveInitialStep(selection));
  // Length chosen before any slot exists; read through `lessonLength` below.
  const [chosenLength, setLessonLengthState] = useState<number>(() =>
    selection.startTime && selection.endTime
      ? timeStringToMinutes(selection.endTime) - timeStringToMinutes(selection.startTime)
      : DEFAULT_LESSON_DURATION_MINUTES,
  );
  // Display sugar, seeded once from sessionStorage (synchronous — see sessionStorage.ts).
  const [displayExtras, setDisplayExtras] = useState(() => loadLessonDisplayExtras());

  // Staleness guard (as in `BookingContext`'s own restore validation): extras
  // apply only when they still describe the CURRENT selection. Otherwise every
  // screen falls back to the URL alone ("Bay {id}", "Your instructor").
  const instructorMatches = displayExtras?.instructorId === selection.instructorId;
  const bayMatches = selection.bayId === null || displayExtras?.bayId === selection.bayId;
  const instructorName = instructorMatches ? (displayExtras?.instructorName ?? null) : null;
  const instructorImage = instructorMatches ? (displayExtras?.instructorImage ?? null) : null;
  const bayName = instructorMatches && bayMatches ? (displayExtras?.bayName ?? null) : null;

  const goToStep = useCallback((next: 0 | 1 | 2) => setStep(next), []);

  // Rule 2 — every mutator writes the URL in the same call.
  const writeParams = useCallback(
    (next: LessonFlowSelection) => {
      setParams({
        [LESSON_SEARCH_PARAMS.INSTRUCTOR_ID]: next.instructorId,
        [LESSON_SEARCH_PARAMS.BAY_ID]: next.bayId,
        [LESSON_SEARCH_PARAMS.DATE]: next.date,
        [LESSON_SEARCH_PARAMS.START_TIME]: next.startTime,
        [LESSON_SEARCH_PARAMS.END_TIME]: next.endTime,
      });
    },
    [setParams],
  );

  const selectInstructor = useCallback(
    (instructor: { id: number; name: string; image: ImageData | null }) => {
      // Rule 4 — a new instructor invalidates everything resolved against
      // the old one's schedule.
      const next: LessonFlowSelection = {
        instructorId: instructor.id,
        bayId: null,
        date: null,
        startTime: null,
        endTime: null,
      };
      setSelection(next);
      writeParams(next);
      const extras = {
        instructorId: instructor.id,
        instructorName: instructor.name,
        instructorImage: instructor.image,
      };
      saveLessonDisplayExtras(extras);
      setDisplayExtras(extras);
    },
    [writeParams],
  );

  const selectBay = useCallback(
    (bay: { id: number; name: string }) => {
      const next: LessonFlowSelection = { ...selection, bayId: bay.id };
      setSelection(next);
      writeParams(next);
      const extras = { ...(displayExtras ?? {}), bayId: bay.id, bayName: bay.name };
      saveLessonDisplayExtras(extras);
      setDisplayExtras(extras);
    },
    [selection, displayExtras, writeParams],
  );

  const setDate = useCallback(
    (date: DateString) => {
      // Rule 4 — times and bays are date-specific.
      const next: LessonFlowSelection = { ...selection, date, startTime: null, endTime: null, bayId: null };
      setSelection(next);
      writeParams(next);
      const extras = { ...displayExtras, bayId: undefined, bayName: undefined };
      saveLessonDisplayExtras(extras);
      setDisplayExtras(extras);
    },
    [selection, displayExtras, writeParams],
  );

  const setTimeRange = useCallback(
    (range: { startTime: string; endTime: string } | null, onDate?: DateString) => {
      const rangeChanged = range?.startTime !== selection.startTime || range?.endTime !== selection.endTime;
      const next: LessonFlowSelection = {
        ...selection,
        // Only fills an unset date; a chosen date is never replaced from here.
        date: selection.date ?? onDate ?? null,
        startTime: range?.startTime ?? null,
        endTime: range?.endTime ?? null,
        bayId: rangeChanged ? null : selection.bayId,
      };
      setSelection(next);
      writeParams(next);
      if (rangeChanged) {
        // Rule 4 — a bay is chosen for a slot.
        const extras = { ...displayExtras, bayId: undefined, bayName: undefined };
        saveLessonDisplayExtras(extras);
        setDisplayExtras(extras);
      }
    },
    [selection, displayExtras, writeParams],
  );

  const setLessonLength = useCallback(
    (minutes: number) => {
      setLessonLengthState(minutes);
      setTimeRange(null);
    },
    [setTimeRange],
  );

  const clearSelection = useCallback(() => {
    const next: LessonFlowSelection = {
      instructorId: null,
      bayId: null,
      date: null,
      startTime: null,
      endTime: null,
    };
    setSelection(next);
    writeParams(next);
    clearLessonDisplayExtras();
    setDisplayExtras(null);
    setStep(0);
  }, [writeParams]);

  // Derived in render, never stored — uses the sentinel-aware minute grammar
  // (slot-times.md) rather than a lexical or Date-based comparison.
  const durationMinutes = useMemo(() => {
    if (!selection.startTime || !selection.endTime) return null;
    return timeStringToMinutes(selection.endTime) - timeStringToMinutes(selection.startTime);
  }, [selection.startTime, selection.endTime]);

  // Once a slot is picked the range is the truth, so the length follows it.
  const lessonLength = durationMinutes ?? chosenLength;

  const value: LessonFlowContextValue = {
    ...selection,
    instructorName,
    instructorImage,
    bayName,
    step,
    goToStep,
    selectInstructor,
    selectBay,
    setDate,
    setTimeRange,
    clearSelection,
    durationMinutes,
    lessonLength,
    setLessonLength,
  };

  return <LessonFlowContext.Provider value={value}>{children}</LessonFlowContext.Provider>;
}
